import { lazy, Suspense, useEffect, useState } from "react";
import { TooltipProvider } from "@/components/ui/tooltip";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import ScrollToTop from "@/components/ScrollToTop";
import BehaviorTracking from "@/components/marketing/BehaviorTracking";
import StickyMobileCTA from "@/components/layout/StickyMobileCTA";
import { I18nProvider } from "@/lib/i18n";
import { Loader2 } from "lucide-react";

// Critical pages loaded eagerly (above-the-fold)
import Index from "./pages/Index";
import NotFound from "./pages/NotFound";

// Deferred toasters (not needed for LCP)
const Toaster = lazy(() => import("@/components/ui/toaster").then(m => ({ default: m.Toaster })));
const Sonner = lazy(() => import("@/components/ui/sonner").then(m => ({ default: m.Toaster })));

// Lazy-loaded pages for better initial bundle size
const Services = lazy(() => import("./pages/Services"));
const About = lazy(() => import("./pages/About"));
const Contact = lazy(() => import("./pages/Contact"));
const Industries = lazy(() => import("./pages/Industries"));
const Locations = lazy(() => import("./pages/Locations"));
const Projects = lazy(() => import("./pages/Projects"));
const Careers = lazy(() => import("./pages/Careers"));
const Resources = lazy(() => import("./pages/Resources"));
const BlogList = lazy(() => import("./pages/BlogList"));
const BlogPost = lazy(() => import("./pages/BlogPost"));
// Every path to a quote ends in booking a call; /quote stays live for old links and search results.
const Privacy = lazy(() => import("./pages/Privacy"));
const TopicGuide = lazy(() => import("./pages/guides/TopicGuide"));
const Terms = lazy(() => import("./pages/Terms"));

const QuoteToBookACall = () => {
  const { pathname, search } = useLocation();
  const prefix = pathname.startsWith("/fr") ? "/fr" : pathname.startsWith("/en") ? "/en" : "";
  return <Navigate to={`${prefix}/book-a-call${search}`} replace />;
};
const Start = lazy(() => import("./pages/Start"));
const BookCall = lazy(() => import("./pages/BookCall"));
const Partnerships = lazy(() => import("./pages/Partnerships"));
const CareersApply = lazy(() => import("./pages/CareersApply"));

// Service Main Pages (lazy)
const ConstructionGeneralContracting = lazy(() => import("./pages/services/ConstructionGeneralContracting"));
const WarehouseAutomation = lazy(() => import("./pages/services/WarehouseAutomation"));
const StorageRacking = lazy(() => import("./pages/services/StorageRacking"));
const Installations = lazy(() => import("./pages/services/Installations"));
const Relocations = lazy(() => import("./pages/services/Relocations"));
const FacilityMaintenance = lazy(() => import("./pages/services/FacilityMaintenance"));
const Inspections = lazy(() => import("./pages/services/Inspections"));
const ResidentServices = lazy(() => import("./pages/services/ResidentServices"));
const ElectricalServices = lazy(() => import("./pages/services/ElectricalServices"));

// About Subpages (lazy)
const SafetyProgram = lazy(() => import("./pages/about/SafetyProgram"));
const QualityAssurance = lazy(() => import("./pages/about/QualityAssurance"));
const Certifications = lazy(() => import("./pages/about/Certifications"));

// Subpages (lazy)
const ServiceSubpage = lazy(() => import("./pages/services/ServiceSubpage"));
const RackingAutomation = lazy(() => import("./pages/services/RackingAutomation"));
const Products = lazy(() => import("./pages/Products"));
const SellYourEquipment = lazy(() => import("./pages/SellYourEquipment"));
const ServiceLocationPage = lazy(() => import("./pages/locations/ServiceLocationPage"));
const AdminInventory = lazy(() => import("./pages/admin/AdminInventory"));
const IndustrySubpage = lazy(() => import("./pages/industries/IndustrySubpage"));
const LocationSubpage = lazy(() => import("./pages/locations/LocationSubpage"));

// Admin Pages (lazy - rarely accessed)
const AdminAuth = lazy(() => import("./pages/admin/AdminAuth"));
const AdminLayout = lazy(() => import("./components/admin/AdminLayout").then(m => ({ default: m.AdminLayout })));
const AdminOverview = lazy(() => import("./pages/admin/AdminOverview"));
const AdminLeads = lazy(() => import("./pages/admin/AdminLeads"));
const AdminLeadDetail = lazy(() => import("./pages/admin/AdminLeadDetail"));
const AdminPipeline = lazy(() => import("./pages/admin/AdminPipeline"));
const AdminPages = lazy(() => import("./pages/admin/AdminPagesEnhanced"));
const AdminPageEditor = lazy(() => import("./pages/admin/AdminPageEditor"));
const AdminPagesSeed = lazy(() => import("./pages/admin/AdminPagesSeed"));
const AdminBlog = lazy(() => import("./pages/admin/AdminBlog"));
const AdminBlogEdit = lazy(() => import("./pages/admin/AdminBlogEdit"));
const AdminTestimonials = lazy(() => import("./pages/admin/AdminTestimonials"));
const AdminMedia = lazy(() => import("./pages/admin/AdminMedia"));
const AdminSettings = lazy(() => import("./pages/admin/AdminSettings"));
const AdminNewsletter = lazy(() => import("./pages/admin/AdminNewsletter"));
const AdminResources = lazy(() => import("./pages/admin/AdminResources"));
const AdminSocial = lazy(() => import("./pages/admin/AdminSocial"));
const AdminAnalytics = lazy(() => import("./pages/admin/AdminAnalytics"));
const AdminRoles = lazy(() => import("./pages/admin/AdminRoles"));

const RebeccaChatBot = lazy(() => import("@/components/chat/MayaChatBot"));
const CookieConsent = lazy(() => import("@/components/marketing/CookieConsent").then((m) => ({ default: m.CookieConsent })));
const AnalyticsProvider = lazy(() => import("@/components/marketing/AnalyticsProvider").then((m) => ({ default: m.AnalyticsProvider })));

// Loading fallback for lazy components
const PageLoader = () => (
  <div className="min-h-screen flex items-center justify-center bg-background">
    <Loader2 className="h-8 w-8 animate-spin text-primary" />
  </div>
);

const App = () => {
  const [loadDeferredWidgets, setLoadDeferredWidgets] = useState(false);

  useEffect(() => {
    if ((window as Window & { __MTLI_PRERENDER?: boolean }).__MTLI_PRERENDER === true) {
      return;
    }

    const idleWindow = window as Window & {
      requestIdleCallback?: (callback: () => void, options?: { timeout: number }) => number;
      cancelIdleCallback?: (id: number) => void;
    };
    let idleId: number | undefined;
    let timeoutId: number | undefined;
    let cancelled = false;

    const load = () => {
      if (!cancelled) setLoadDeferredWidgets(true);
    };

    const schedule = () => {
      if (idleWindow.requestIdleCallback) {
        idleId = idleWindow.requestIdleCallback(load, { timeout: 3000 });
      } else {
        timeoutId = window.setTimeout(load, 2000);
      }
    };

    if (document.readyState === "complete") schedule();
    else window.addEventListener("load", schedule, { once: true });

    return () => {
      cancelled = true;
      window.removeEventListener("load", schedule);
      if (idleId !== undefined && idleWindow.cancelIdleCallback) idleWindow.cancelIdleCallback(idleId);
      if (timeoutId !== undefined) window.clearTimeout(timeoutId);
    };
  }, []);

  return (
    <I18nProvider>
      <TooltipProvider>
        {loadDeferredWidgets && (
          <Suspense fallback={null}>
            <Toaster />
            <Sonner />
          </Suspense>
        )}
        <BrowserRouter>
          <ScrollToTop />
          <BehaviorTracking />
          <Suspense fallback={<PageLoader />}>
            <Routes>
              {/* Canadian routes with language prefix */}
              <Route path="/en" element={<Index />} />
              <Route path="/fr" element={<Index />} />
              <Route path="/en/*" element={<CanadianRoutes />} />
              <Route path="/fr/*" element={<CanadianRoutes />} />
              
              {/* Default routes (work for both CA default and US) */}
              <Route path="/" element={<Index />} />
              <Route path="/quote" element={<QuoteToBookACall />} />
    <Route path="/guides/:slug" element={<TopicGuide />} />
              <Route path="/guides/:slug" element={<TopicGuide />} />
    <Route path="/privacy" element={<Privacy />} />
    <Route path="/terms" element={<Terms />} />
              <Route path="/privacy" element={<Privacy />} />
              <Route path="/terms" element={<Terms />} />
              <Route path="/start" element={<Start />} />
              <Route path="/book-a-call" element={<BookCall />} />
              <Route path="/partnerships" element={<Partnerships />} />
              <Route path="/careers/apply" element={<CareersApply />} />
              <Route path="/products" element={<Products />} />
              <Route path="/sell-your-equipment" element={<SellYourEquipment />} />
              <Route path="/services" element={<Services />} />
              <Route path="/services/commercial-construction" element={<ConstructionGeneralContracting />} />
              <Route path="/services/construction-general-contracting" element={<Navigate to="/services/commercial-construction" replace />} />
              <Route path="/services/warehouse-automation" element={<WarehouseAutomation />} />
              <Route path="/services/warehouse-racking-systems" element={<StorageRacking />} />
              <Route path="/services/storage-racking" element={<Navigate to="/services/warehouse-racking-systems" replace />} />
              <Route path="/services/installations" element={<Installations />} />
              <Route path="/services/commercial-relocation-services" element={<Relocations />} />
              <Route path="/services/relocations" element={<Navigate to="/services/commercial-relocation-services" replace />} />
              <Route path="/services/facility-maintenance" element={<FacilityMaintenance />} />
              <Route path="/services/inspections" element={<Inspections />} />
              <Route path="/services/resident-services" element={<ResidentServices />} />
              <Route path="/services/electrical-services" element={<Navigate to="/services/electrical-automation-services" replace />} />
              <Route path="/services/electrical-automation-services" element={<ElectricalServices />} />
              {/* Construction Subpages */}
              <Route path="/services/commercial-construction/:subpage" element={<ServiceSubpage />} />
              <Route path="/services/construction-general-contracting/:subpage" element={<ServiceSubpage />} />
              {/* Automation Subpages */}
              <Route path="/services/warehouse-automation/:subpage" element={<ServiceSubpage />} />
              {/* Racking Subpages */}
              <Route path="/services/warehouse-racking-systems/:subpage" element={<ServiceSubpage />} />
              <Route path="/services/racking-automation" element={<RackingAutomation />} />
              <Route path="/services/storage-racking/:subpage" element={<ServiceSubpage />} />
              {/* Installation Subpages */}
              <Route path="/services/installations/:subpage" element={<ServiceSubpage />} />
              <Route path="/services/relocations/:subpage" element={<ServiceSubpage />} />
              <Route path="/services/facility-maintenance/:subpage" element={<ServiceSubpage />} />
              <Route path="/about" element={<About />} />
              <Route path="/about/safety" element={<SafetyProgram />} />
              <Route path="/about/quality" element={<QualityAssurance />} />
              <Route path="/about/certifications" element={<Certifications />} />
              <Route path="/contact" element={<Contact />} />
              <Route path="/industries" element={<Industries />} />
              {/* Industry Subpages */}
              <Route path="/industries/:slug" element={<IndustrySubpage />} />
              <Route path="/locations" element={<Locations />} />
              {/* Location Subpages */}
              <Route path="/locations/:locationSlug/:serviceSlug" element={<ServiceLocationPage />} />
              <Route path="/locations/:slug" element={<LocationSubpage />} />
              <Route path="/projects" element={<Projects />} />
              <Route path="/careers" element={<Careers />} />
              <Route path="/resources" element={<Resources />} />
              <Route path="/blog" element={<BlogList />} />
              <Route path="/blog/:slug" element={<BlogPost />} />
              {/* Admin Routes */}
              <Route path="/admin/auth" element={<AdminAuth />} />
              <Route path="/admin" element={<AdminLayout />}>
                <Route index element={<AdminOverview />} />
                <Route path="leads" element={<AdminLeads />} />
                <Route path="leads/:id" element={<AdminLeadDetail />} />
                <Route path="pipeline" element={<AdminPipeline />} />
                <Route path="pages" element={<AdminPages />} />
                <Route path="pages/seed" element={<AdminPagesSeed />} />
                <Route path="pages/:id" element={<AdminPageEditor />} />
                <Route path="blog" element={<AdminBlog />} />
                <Route path="blog/:id" element={<AdminBlogEdit />} />
                <Route path="resources" element={<AdminResources />} />
                <Route path="inventory" element={<AdminInventory />} />
                <Route path="testimonials" element={<AdminTestimonials />} />
                <Route path="media" element={<AdminMedia />} />
                <Route path="settings" element={<AdminSettings />} />
                <Route path="newsletter" element={<AdminNewsletter />} />
                <Route path="social" element={<AdminSocial />} />
                <Route path="analytics" element={<AdminAnalytics />} />
                <Route path="roles" element={<AdminRoles />} />
              </Route>
              {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
              <Route path="*" element={<NotFound />} />
            </Routes>
          </Suspense>
          <StickyMobileCTA />
          {loadDeferredWidgets && (
            <Suspense fallback={null}>
              <RebeccaChatBot />
              <CookieConsent />
              <AnalyticsProvider />
            </Suspense>
          )}
        </BrowserRouter>
      </TooltipProvider>
    </I18nProvider>
  );
};

// Canadian routes component to handle /en/* and /fr/* paths
const CanadianRoutes = () => (
  <Routes>
    <Route path="/" element={<Index />} />
    <Route path="/quote" element={<QuoteToBookACall />} />
    <Route path="/products" element={<Products />} />
              <Route path="/sell-your-equipment" element={<SellYourEquipment />} />
              <Route path="/services" element={<Services />} />
    <Route path="/services/commercial-construction" element={<ConstructionGeneralContracting />} />
    <Route path="/services/construction-general-contracting" element={<Navigate to="/services/commercial-construction" replace />} />
    <Route path="/services/warehouse-automation" element={<WarehouseAutomation />} />
    <Route path="/services/warehouse-racking-systems" element={<StorageRacking />} />
    <Route path="/services/storage-racking" element={<Navigate to="/services/warehouse-racking-systems" replace />} />
    <Route path="/services/installations" element={<Installations />} />
    <Route path="/services/commercial-relocation-services" element={<Relocations />} />
    <Route path="/services/relocations" element={<Navigate to="/services/commercial-relocation-services" replace />} />
    <Route path="/services/facility-maintenance" element={<FacilityMaintenance />} />
    <Route path="/services/inspections" element={<Inspections />} />
    <Route path="/services/resident-services" element={<ResidentServices />} />
    <Route path="/services/electrical-services" element={<Navigate to="/services/electrical-automation-services" replace />} />
    <Route path="/services/electrical-automation-services" element={<ElectricalServices />} />
    <Route path="/services/commercial-construction/:subpage" element={<ServiceSubpage />} />
    <Route path="/services/construction-general-contracting/:subpage" element={<ServiceSubpage />} />
    <Route path="/services/warehouse-automation/:subpage" element={<ServiceSubpage />} />
    <Route path="/services/warehouse-racking-systems/:subpage" element={<ServiceSubpage />} />
    <Route path="/services/racking-automation" element={<RackingAutomation />} />
              <Route path="/services/storage-racking/:subpage" element={<ServiceSubpage />} />
    <Route path="/services/installations/:subpage" element={<ServiceSubpage />} />
              <Route path="/services/relocations/:subpage" element={<ServiceSubpage />} />
              <Route path="/services/facility-maintenance/:subpage" element={<ServiceSubpage />} />
    <Route path="/about" element={<About />} />
    <Route path="/about/safety" element={<SafetyProgram />} />
    <Route path="/about/quality" element={<QualityAssurance />} />
    <Route path="/about/certifications" element={<Certifications />} />
    <Route path="/contact" element={<Contact />} />
    <Route path="/industries" element={<Industries />} />
    <Route path="/industries/:slug" element={<IndustrySubpage />} />
    <Route path="/locations" element={<Locations />} />
    <Route path="/locations/:locationSlug/:serviceSlug" element={<ServiceLocationPage />} />
              <Route path="/locations/:slug" element={<LocationSubpage />} />
    <Route path="/projects" element={<Projects />} />
    <Route path="/careers" element={<Careers />} />
    <Route path="/resources" element={<Resources />} />
    <Route path="/blog" element={<BlogList />} />
    <Route path="/blog/:slug" element={<BlogPost />} />
    <Route path="*" element={<NotFound />} />
  </Routes>
);

export default App;
